import type {Camera,Material,Object3D,Scene,WebGLRenderer} from 'three';

// Three r169's internal program wrapper. Its destroy() clears `program`;
// isReady() queries only KHR_parallel_shader_compile completion, never gl.finish.
interface PendingProgram {program:unknown;isReady():boolean}
type CompileRenderer=Pick<WebGLRenderer,'compile'|'properties'|'extensions'>;

/** Three's parallel shader warmup with ownership-safe polling.
 * r169 compileAsync rereads material properties after disposal has deleted them.
 * Keep the compiled program references instead, discard disposed materials, and
 * stop if the object leaves its owner. No render, uniform lookup or sync finish.
 */
export function compileObjectAsync<T extends Object3D>(renderer:CompileRenderer,object:T,camera:Camera,targetScene?:Scene|null,signal?:AbortSignal):Promise<T>{
 return new Promise<T>((resolve,reject)=>{
  const pending=new Map<Material,PendingProgram>(),listeners=new Map<Material,()=>void>(),disposed=new Set<Material>();
  let timer:ReturnType<typeof setTimeout>|undefined,done=false,initialized=false;
  const cleanup=()=>{
   if(timer!==undefined){clearTimeout(timer);timer=undefined;}
   object.removeEventListener('removed',cancel);signal?.removeEventListener('abort',cancel);
   for(const [material,listener]of listeners)material.removeEventListener('dispose',listener);
   pending.clear();listeners.clear();disposed.clear();
  };
  const finish=(error?:unknown)=>{if(done)return;done=true;cleanup();if(error!==undefined)reject(error);else resolve(object);};
  const cancel=()=>{const error=new Error('Shader warmup cancelled');error.name='AbortError';finish(error);};
  const watch=(material:Material)=>{
   if(listeners.has(material))return;
   const listener=()=>{disposed.add(material);pending.delete(material);if(initialized&&pending.size===0)finish();};
   listeners.set(material,listener);material.addEventListener('dispose',listener);
  };
  const poll=()=>{
   timer=undefined;if(done)return;
   try{
    for(const [material,program]of pending){
     // Renderer/context disposal can destroy a program without a material event.
     if(program.program===undefined||program.program===null||program.isReady())pending.delete(material);
    }
    if(pending.size===0)finish();else timer=setTimeout(poll,10);
   }catch(error){finish(error);}
  };
  if(signal?.aborted){cancel();return;}
  object.addEventListener('removed',cancel);signal?.addEventListener('abort',cancel,{once:true});
  try{
   // Listen before onBeforeCompile hooks run, including synchronous disposal.
   object.traverse(child=>{const material=(child as Object3D&{material?:Material|Material[]}).material;if(Array.isArray(material))material.forEach(watch);else if(material)watch(material);});
   const materials=renderer.compile(object,camera,targetScene);
   if(done)return;
   for(const material of materials){
    watch(material);
    // has() avoids re-creating the empty property record removed by disposal.
    if(disposed.has(material)||!renderer.properties.has(material))continue;
    const program=(renderer.properties.get(material) as {currentProgram?:PendingProgram}).currentProgram;
    if(program?.program!==undefined&&program.program!==null)pending.set(material,program);
   }
   initialized=true;
   if(pending.size===0){finish();return;}
   if(renderer.extensions.get('KHR_parallel_shader_compile')!==null)poll();
   else timer=setTimeout(poll,10); // Same non-extension fallback as Three r169.
  }catch(error){finish(error);}
 });
}
